import { zodResolver } from "@hookform/resolvers/zod";
import { type SetStateAction, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import useSWR from "swr";
import { simpleFetcher } from "~/api/fetcher";
import { type WorkflowStepInput, type WorkflowTemplate } from "~/api/types";
import {
  applyWorkflowTemplate,
  updateDocumentRelease,
} from "~/api/v2/requests";
import { useDocumentRelease } from "~/api/v2/useDocumentRelease";
import { useWorkflowTemplates } from "~/api/v2/useWorkflowTemplates";
import { PlatformRole } from "~/config/types";
import { canEditWorkflow } from "~/helpers/documentPermissions";
import { mapStepInputsToWorkflowSteps } from "~/helpers/workflow";
import {
  buildWorkflowDateChain,
  findOutOfOrderDates,
} from "~/helpers/workflowDates";
import { useUserContext } from "~/hooks/useUserContext";
import { useWorkflowUsers } from "~/hooks/useWorkflowUsers";
import {
  mapFormValuesToUpdateReleaseData,
  mapReleaseToFormValues,
  type WorkflowEditorFormValues,
  workflowEditorFormSchema,
} from "./workflowEditorForm";
import { type DocumentReleaseDetails } from "~/api/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

export type WorkflowEditorMode =
  | {
      type: "editing";
      release: DocumentReleaseDetails;
      steps: WorkflowStepInput[];
    }
  | {
      type: "previewing";
      release: DocumentReleaseDetails;
      steps: WorkflowStepInput[];
      description: string;
    };

export function useWorkflowEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const isWorkflowTemplatesEnabled = useFeatureFlagEnabled(
    featureFlags.workflowTemplates,
  );

  const canManagePlatform =
    user.role === PlatformRole.coordinator ||
    user.role === PlatformRole.superAdmin;

  const { data: release, isLoading, mutate } = useDocumentRelease(id);
  const { data: templates } = useWorkflowTemplates();
  const { hydrateSteps } = useWorkflowUsers();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [isApplying, setIsApplying] = useState(false);

  const templateDetailUrl = selectedTemplateId
    ? `/api/v2/workflow-templates/${selectedTemplateId}`
    : null;

  const { data: templateDetail } = useSWR<WorkflowTemplate>(
    templateDetailUrl,
    simpleFetcher,
  );

  const form = useForm<WorkflowEditorFormValues>({
    resolver: zodResolver(workflowEditorFormSchema),
    values: mapReleaseToFormValues(release),
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const editedSteps = useWatch({
    control: form.control,
    name: "steps",
  });

  const reviewFrom = useWatch({ control: form.control, name: "reviewFrom" });
  const reviewTo = useWatch({ control: form.control, name: "reviewTo" });

  const hasDateOrderError =
    findOutOfOrderDates(
      buildWorkflowDateChain(editedSteps ?? [], reviewFrom, reviewTo),
    ).size > 0;

  const isPreviewMode = selectedTemplateId != null && templateDetail != null;

  const previewSteps =
    (templateDetail ? hydrateSteps(templateDetail) : null) ?? [];

  const templateOptions =
    templates?.map((tmpl) => ({
      value: String(tmpl.id),
      label: tmpl.name ?? t(($) => $.workflowTemplates.defaultBadge),
    })) ?? [];

  const canEditCurrentWorkflow = release ? canEditWorkflow(release) : false;

  const showTemplateDropdown =
    !!release &&
    canEditCurrentWorkflow &&
    !release.artifact.isOngoing &&
    canManagePlatform &&
    isWorkflowTemplatesEnabled === true;

  const mode: WorkflowEditorMode | null = (() => {
    if (!release) return null;

    if (isPreviewMode) {
      return {
        type: "previewing" as const,
        release: {
          ...release,
          artifact: {
            ...release.artifact,
            isOngoing: false,
            steps: mapStepInputsToWorkflowSteps(previewSteps),
            description: templateDetail.description,
            stepInstances: [],
          },
        },
        steps: previewSteps,
        description: templateDetail?.description ?? "",
      };
    }

    return {
      type: "editing" as const,
      release,
      steps: editedSteps ?? [],
    };
  })();

  const hasUnsavedChanges = form.formState.isDirty;

  const setEditedSteps = (value: SetStateAction<WorkflowStepInput[]>) => {
    const current = form.getValues("steps");
    const nextValue = typeof value === "function" ? value(current) : value;

    form.setValue("steps", nextValue, { shouldDirty: true });
  };

  const handleSave = form.handleSubmit(
    async (values) => {
      if (!release) {
        return;
      }

      try {
        await updateDocumentRelease(
          release.id,
          mapFormValuesToUpdateReleaseData(values, form.formState.dirtyFields),
        );
        form.reset(values);
        toast.success(t(($) => $.commons.workflow.workflowUpdatedSuccessfully));
        await mutate();
        void navigate(-1);
      } catch (error) {
        console.error(
          t(($) => $.commons.workflow.failedToUpdateWorkflow),
          error,
        );
        toast.error(t(($) => $.commons.workflow.failedToUpdateWorkflow));
      }
    },
    (errors) => {
      const firstError = Object.values(errors).find((error) => error?.message);
      if (firstError?.message) {
        toast.error(firstError.message);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    },
  );

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !release) {
      return;
    }

    setIsApplying(true);
    try {
      await applyWorkflowTemplate(Number(selectedTemplateId), {
        artifactIds: [release.artifact.id],
      });
      toast.success(t(($) => $.workflowTemplates.applyOnWorkflow.success));
      setSelectedTemplateId(null);

      const updatedRelease = await mutate();
      if (updatedRelease) {
        form.reset(mapReleaseToFormValues(updatedRelease));
      }
    } catch (error) {
      console.error("Failed to apply template", error);
      toast.error(t(($) => $.workflowTemplates.applyOnWorkflow.error));
    } finally {
      setIsApplying(false);
    }
  };

  const handleTemplateSelect = (value: string) => {
    setSelectedTemplateId((current) => (value === current ? null : value));
  };

  return {
    isLoading,
    mode,
    selectedTemplateId,
    isApplying,
    showTemplateDropdown,
    templateOptions,
    form,
    hasUnsavedChanges,
    hasDateOrderError,
    handleTemplateSelect,
    handleApplyTemplate,
    handleCancelPreview: () => setSelectedTemplateId(null),
    setEditedSteps,
    handleSave,
  };
}
